import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { CaretLeft, CaretRight, Plus, Trash, ClipboardText, Question, VideoCamera, CalendarBlank } from "@phosphor-icons/react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import EmptyState from "@/components/EmptyState";

const TYPE_META = {
  devoir: { label: "Devoir", icon: ClipboardText, dot: "bg-accent" },
  quiz: { label: "Quiz", icon: Question, dot: "bg-[hsl(var(--campus-orange))]" },
  session: { label: "Session en visio", icon: VideoCamera, dot: "bg-primary" },
  examen: { label: "Examen", icon: ClipboardText, dot: "bg-[hsl(var(--campus-green))]" },
  autre: { label: "Événement", icon: CalendarBlank, dot: "bg-muted-foreground" },
};

const WEEKDAYS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];
const MONTHS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

function dayKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function itemDayKey(item) {
  return item.date.slice(0, 10);
}

export default function Calendrier() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canManage = user.role === "admin" || user.role === "professeur";

  const [items, setItems] = useState([]);
  const [courses, setCourses] = useState([]);
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [selectedDay, setSelectedDay] = useState(dayKey(new Date()));
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", course_id: "", start_at: "", kind: "session" });

  const load = () => api.get("/calendar").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
    if (canManage) api.get("/courses").then((r) => setCourses(r.data)).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const byDay = useMemo(() => {
    const map = {};
    for (const it of items) {
      const k = itemDayKey(it);
      if (!map[k]) map[k] = [];
      map[k].push(it);
    }
    for (const k in map) map[k].sort((a, b) => a.date.localeCompare(b.date));
    return map;
  }, [items]);

  const upcoming = useMemo(() => {
    const today = dayKey(new Date());
    return items.filter((it) => itemDayKey(it) >= today).sort((a, b) => a.date.localeCompare(b.date)).slice(0, 8);
  }, [items]);

  const create = async () => {
    if (!form.title.trim() || !form.start_at) return toast.error("Titre et date/heure sont obligatoires.");
    try {
      await api.post("/events", { ...form, course_id: form.course_id || null });
      toast.success("Événement créé");
      setOpen(false);
      setForm({ title: "", description: "", course_id: "", start_at: "", kind: "session" });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (raw_id) => {
    try { await api.delete(`/events/${raw_id}`); toast.success("Événement supprimé"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  // grille du mois (lundi en premier)
  const firstOfMonth = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const startOffset = (firstOfMonth.getDay() + 6) % 7; // 0 = lundi
  const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));

  const changeMonth = (delta) => setCursor((c) => new Date(c.getFullYear(), c.getMonth() + delta, 1));
  const todayKey = dayKey(new Date());
  const selectedItems = byDay[selectedDay] || [];

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">Organisation</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Calendrier</h1>
          <p className="text-sm text-muted-foreground">Dates limites de devoirs et quiz, sessions en visio et événements, tous centralisés ici.</p>
        </div>
        {canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-primary hover:bg-primary/90" data-testid="add-event-btn"><Plus size={18} className="mr-2" /> Nouvel événement</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Planifier un événement</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>Titre</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Session Zoom, examen, réunion..." data-testid="event-title" /></div>
                <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
                <div className="flex gap-3">
                  <div className="flex-1">
                    <Label>Date et heure</Label>
                    <Input type="datetime-local" value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })} data-testid="event-datetime" />
                  </div>
                  <div className="w-40">
                    <Label>Type</Label>
                    <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="session">Session en visio</SelectItem>
                        <SelectItem value="examen">Examen</SelectItem>
                        <SelectItem value="autre">Autre</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Cours concerné (optionnel)</Label>
                  <Select value={form.course_id || "__none"} onValueChange={(v) => setForm({ ...form, course_id: v === "__none" ? "" : v })}>
                    <SelectTrigger data-testid="event-course"><SelectValue placeholder="Aucun — événement général" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none">Aucun — événement général</SelectItem>
                      {courses.map((c) => <SelectItem key={c.id} value={c.id}>{c.title}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="mt-1 text-xs text-muted-foreground">Sans cours choisi, l'événement est visible de tous. Avec un cours, seuls les étudiants ayant accès à ce cours le verront.</p>
                </div>
              </div>
              <DialogFooter><Button onClick={create} className="bg-primary hover:bg-primary/90" data-testid="save-event-btn">Planifier</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="flex flex-wrap gap-3 text-xs">
        {Object.entries(TYPE_META).map(([key, meta]) => (
          <span key={key} className="flex items-center gap-1.5 text-muted-foreground"><span className={`h-2.5 w-2.5 rounded-full ${meta.dot}`} /> {meta.label}</span>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="rounded-xl border border-border bg-card p-5">
          <div className="mb-4 flex items-center justify-between">
            <Button size="icon" variant="ghost" onClick={() => changeMonth(-1)} data-testid="prev-month"><CaretLeft size={18} /></Button>
            <h2 className="font-display text-lg font-bold">{MONTHS[cursor.getMonth()]} {cursor.getFullYear()}</h2>
            <Button size="icon" variant="ghost" onClick={() => changeMonth(1)} data-testid="next-month"><CaretRight size={18} /></Button>
          </div>
          <div className="grid grid-cols-7 gap-1 text-center text-xs font-medium text-muted-foreground">
            {WEEKDAYS.map((w) => <div key={w} className="py-1">{w}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1">
            {cells.map((d, i) => {
              if (!d) return <div key={i} />;
              const k = dayKey(d);
              const dayItems = byDay[k] || [];
              const isToday = k === todayKey;
              const isSelected = k === selectedDay;
              return (
                <button
                  key={i}
                  onClick={() => setSelectedDay(k)}
                  className={`flex h-16 flex-col items-center rounded-lg border p-1 text-sm transition-colors ${isSelected ? "border-primary bg-primary/10" : "border-transparent hover:bg-secondary"}`}
                  data-testid={`day-${k}`}
                >
                  <span className={`flex h-6 w-6 items-center justify-center rounded-full ${isToday ? "bg-primary font-bold text-primary-foreground" : ""}`}>{d.getDate()}</span>
                  <div className="mt-1 flex gap-0.5">
                    {dayItems.slice(0, 4).map((it, idx) => (
                      <span key={idx} className={`h-1.5 w-1.5 rounded-full ${TYPE_META[it.type]?.dot || "bg-muted-foreground"}`} />
                    ))}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-xl border border-border bg-card p-5">
            <h3 className="font-display font-bold">{new Date(selectedDay).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}</h3>
            <div className="mt-3 space-y-2">
              {selectedItems.length === 0 && <p className="text-sm text-muted-foreground">Rien de prévu ce jour-là.</p>}
              {selectedItems.map((it, idx) => (
                <EventRow key={it.id} item={it} index={idx} onNavigate={navigate} onDelete={del} />
              ))}
            </div>
          </div>

          <div className="rounded-xl border border-border bg-card p-5">
            <h3 className="font-display font-bold">À venir</h3>
            <div className="mt-3 space-y-2">
              {upcoming.length === 0 ? (
                <EmptyState icon={CalendarBlank} title="Rien de planifié" description="Aucune échéance à venir pour le moment." />
              ) : upcoming.map((it, idx) => <EventRow key={it.id} item={it} compact index={idx} onNavigate={navigate} onDelete={del} />)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function EventRow({ item, compact, index = 0, onNavigate, onDelete }) {
  const meta = TYPE_META[item.type] || TYPE_META.autre;
  const Icon = meta.icon;
  const dt = new Date(item.date);
  const hasTime = item.date.includes("T");
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.04 }}
      className="flex items-start gap-3 rounded-lg border border-border p-3 text-sm transition-shadow hover:shadow-sm"
    >
      <div className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-white ${meta.dot}`}>
        <Icon size={16} weight="fill" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium">{item.title}</p>
        <p className="text-xs text-muted-foreground">
          {compact && dt.toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}
          {hasTime && ` · ${dt.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`}
          {item.course_title && ` · ${item.course_title}`}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {item.link && <Button size="sm" variant="ghost" onClick={() => onNavigate(item.link)}>Voir</Button>}
        {item.can_delete && <Button size="icon" variant="ghost" onClick={() => onDelete(item.raw_id)}><Trash size={14} className="text-accent" /></Button>}
      </div>
    </motion.div>
  );
}
